import {
  AlignLeft,
  Award,
  BadgeCheck,
  BarChart3,
  BedDouble,
  Boxes,
  Plane,
  Hotel,
  Briefcase,
  CalendarDays,
  CalendarRange,
  Clapperboard,
  Compass,
  Building2,
  FileText,
  Filter,
  HeartHandshake,
  Info,
  GalleryHorizontal,
  Grid2x2,
  Grid3x3,
  Handshake,
  Image as ImageIcon,
  Images,
  LayoutGrid,
  HelpCircle,
  ListChecks,
  ListOrdered,
  type LucideIcon,
  MapPin,
  MapPinned,
  Megaphone,
  Newspaper,
  PanelTop,
  PhoneCall,
  Quote,
  Sparkles,
  PartyPopper,
  Star,
  Target,
  Ticket,
  Users,
} from 'lucide-react';

/**
 * Static metadata for the Custom Pages section editor: the catalogue of
 * section types (label, icon, hint) and the multi-section presets shown in
 * the "Add a section" drawer. Section type values mirror the Prisma
 * `WebsiteSectionType` enum (UPPER_SNAKE) and the per-type Zod schemas in
 * @utazzo/shared.
 */

export const SECTION_TYPES = [
  'HERO',
  'SERVICE_ICONS_ROW',
  'STAR_ENDORSEMENTS',
  'DESTINATION_SHOWCASE',
  'FEATURED_CATEGORY',
  'EDITORS_PICKS',
  'EXPLORE_STORIES',
  'CTA_BANNER',
  'FEATURE_LIST',
  'VALUE_PROPS',
  'CLIENT_STORIES',
  'REVIEWS',
  'MICE_CLIENT_STORIES',
  'GALLERY',
  'AWARDS_BLOCK',
  'CALLBACK_FORM',
  'MICE_HERO',
  'MICE_FORM',
  'MICE_ACCREDITATIONS',
  'MICE_PARTNERS_MARQUEE',
  'MICE_SERVICES',
  'MICE_CASE_STUDIES',
  'MICE_DESTINATIONS',
  'PROCESS_STEPS',
  'FAQ_LIST',
  'PARTNERS_LOGOS',
  'PRESS_LOGOS',
  'LOCATIONS',
  'TEXT_BLOCK',
  'RICH_TEXT',
  'TEAM_PROFILES',
  'FULL_IMAGE',
  'FEATURE_CARDS',
  'STATS_STRIP',
  'IMAGE_MARQUEE',
  'OPEN_POSITIONS',
  'REELS_STRIP',
  'AWARDS_STATS',
  'VIDEO_REVIEWS',
  'TRAVELLER_REVIEWS',
  'HANDPICKED_STAYS',
  'EXPLORE_THEMES',
  'STAY_COLLECTION',
  'STAY_DESTINATIONS',
  'FLIGHT_DEALS',
  'ACTIVITY_PICKS',
  'PACKAGE_CAROUSELS',
  'QUICK_INFO',
  'BEST_TIME',
  'MONTH_BY_MONTH',
  'TRAVEL_STYLES',
  'RECOMMENDED_PACKAGES',
  'PLACES_GRID',
  'EVENTS_GRID',
  'ACTIVITIES_GRID',
  'VISION_MISSION',
  'CELEBRATION',
  'CALLBACK_CHAT',
  'LANDING_PACKAGES',
] as const;

export type SectionType = (typeof SECTION_TYPES)[number];

export interface SectionMeta {
  label: string;
  icon: LucideIcon;
  hint: string;
  /// Optional grouping for the "Add a section" picker. Types sharing a group
  /// render under a small heading; ungrouped types stay in the default grid.
  group?: string;
}

/// Heading shown above grouped section types in the "Add a section" picker.
export const MICE_GROUP = 'MICE';

/// Grouping for the tourism-board / Discover-Europe specific sections.
export const TOURISM_GROUP = 'Tourism board';

/// Grouping for About Us page sections.
export const ABOUT_GROUP = 'About Us';

/// Grouping for the Reviews page sections (award stats, video reviews,
/// traveller reviews with the live Google feed).
export const REVIEWS_GROUP = 'Reviews page';

/// Grouping for the /hotels, /flights and /activities landing-page sections -
/// the live rows fed by TBO plus the static activity lists.
export const FUNNEL_GROUP = 'Flights, hotels & activities';

export const SECTION_META: Record<SectionType, SectionMeta> = {
  HERO: {
    label: 'Hero',
    icon: ImageIcon,
    hint: 'Full-width banner - video/image, headline + search',
  },
  SERVICE_ICONS_ROW: {
    label: 'Service icons row',
    icon: Grid3x3,
    hint: '1-15 icon tiles in a row',
  },
  STAR_ENDORSEMENTS: {
    label: 'Star endorsements',
    icon: Star,
    hint: '1-10 traveller cards with photo',
  },
  DESTINATION_SHOWCASE: {
    label: 'Category pages showcase',
    icon: MapPinned,
    hint: 'Pick a category page; auto-lists all its destinations (price + count)',
  },
  FEATURED_CATEGORY: {
    label: 'Featured category',
    icon: LayoutGrid,
    hint: '1 featured + up to 3 tiles',
  },
  EDITORS_PICKS: {
    label: "Editor's Picks",
    icon: Sparkles,
    hint: '1 featured blog + up to 4 picked posts',
  },
  EXPLORE_STORIES: {
    label: 'Explore stories',
    icon: Filter,
    hint: 'Faceted story filter - left rail (destination + category) + story grid',
  },
  CTA_BANNER: { label: 'CTA banner', icon: Megaphone, hint: 'Short promotional banner' },
  FEATURE_LIST: { label: 'Feature list', icon: ListChecks, hint: '1-8 feature bullets + photos' },
  VALUE_PROPS: {
    label: "What's in it for you?",
    icon: ListChecks,
    hint: '1-8 value-prop items (title + description) in a two-column grid',
  },
  CLIENT_STORIES: { label: 'Client stories', icon: Quote, hint: '1-20 quote cards with rating' },
  REVIEWS: {
    label: 'Reviews',
    icon: Star,
    hint: '1-20 review cards - star rating, quote, a "View details" link and an avatar + name',
  },
  MICE_CLIENT_STORIES: {
    label: 'Client stories (MICE)',
    icon: Quote,
    hint: 'Image-rich testimonial carousel - each card has a hero image, thumbnails, avatar + name + date, a rating badge and a quote',
    group: MICE_GROUP,
  },
  GALLERY: {
    label: 'Photo gallery',
    icon: Images,
    hint: 'Bulk photo collage with calm floating motion',
  },
  AWARDS_BLOCK: {
    label: 'Awards block',
    icon: Award,
    hint: 'Awards / credentials - timeline or photo cards',
  },
  CALLBACK_FORM: { label: 'Callback form', icon: PhoneCall, hint: 'Lead capture form' },
  MICE_HERO: {
    label: 'MICE hero',
    icon: PanelTop,
    hint: 'Corporate hero - background image, heading + stats, inline lead form on the right',
    group: MICE_GROUP,
  },
  MICE_FORM: {
    label: 'MICE form',
    icon: Building2,
    hint: 'Corporate MICE lead-capture card - company, team size, travel dates',
    group: MICE_GROUP,
  },
  MICE_ACCREDITATIONS: {
    label: 'Accreditations & Partners',
    icon: BadgeCheck,
    hint: 'Static grid of accreditation / partner logos - heading + subhead over a logo grid',
    group: MICE_GROUP,
  },
  MICE_PARTNERS_MARQUEE: {
    label: 'Partners marquee',
    icon: Handshake,
    hint: 'Single-row scrolling marquee of partner / client logos - heading + subhead over a logo strip',
    group: MICE_GROUP,
  },
  MICE_SERVICES: {
    label: 'Our MICE Services',
    icon: Boxes,
    hint: 'Service-pillar grid - each card has an icon, title, description + a bullet list',
    group: MICE_GROUP,
  },
  MICE_CASE_STUDIES: {
    label: 'Case studies',
    icon: Award,
    hint: 'Trusted-brands success-story cards - title, what-we-delivered bullets, stats + images',
    group: MICE_GROUP,
  },
  MICE_DESTINATIONS: {
    label: 'Destinations We Cover',
    icon: MapPinned,
    hint: 'Grouped destination carousels (e.g. Domestic / International MICE) - each a coloured band with a heading + a scrolling row of destination cards',
    group: MICE_GROUP,
  },
  PROCESS_STEPS: {
    label: 'Our Process',
    icon: ListOrdered,
    hint: 'Numbered step cards (01, 02, …) in a grid, with an optional CTA button',
    group: MICE_GROUP,
  },
  FAQ_LIST: {
    label: 'FAQ list',
    icon: HelpCircle,
    hint: 'Expandable question + answer accordion',
  },
  PARTNERS_LOGOS: { label: 'Partners logos', icon: Handshake, hint: '1-40 partner logos' },
  PRESS_LOGOS: { label: 'Press logos', icon: Newspaper, hint: '1-40 press mentions' },
  LOCATIONS: {
    label: 'Our locations',
    icon: MapPin,
    hint: 'Office cards + a Google Maps embed per office',
  },
  TEXT_BLOCK: {
    label: 'Text block',
    icon: AlignLeft,
    hint: 'Heading + multi-paragraph description, optional banner image',
  },
  RICH_TEXT: {
    label: 'Rich text',
    icon: FileText,
    hint: 'WYSIWYG long-form content - Terms, Privacy Policy, About',
  },
  TEAM_PROFILES: {
    label: 'Team profiles',
    icon: Users,
    hint: 'Founder / team cards - photo, name, role, bio',
  },
  FULL_IMAGE: {
    label: 'Full-width image',
    icon: ImageIcon,
    hint: 'Heading + one full-width image (e.g. a group photo)',
  },
  FEATURE_CARDS: {
    icon: Grid2x2,
    label: 'Feature cards',
    hint: '3-column grid of cards - logo, title, subtitle, description (3/6/9)',
  },
  STATS_STRIP: {
    icon: BarChart3,
    label: 'Stats strip',
    hint: 'Bordered bar of figures - value + label (e.g. "100K+" / "Travellers")',
  },
  IMAGE_MARQUEE: {
    icon: GalleryHorizontal,
    label: 'Image marquee',
    hint: 'A row of uploaded images that slides as a continuous marquee',
  },
  OPEN_POSITIONS: {
    icon: Briefcase,
    label: 'Open positions',
    hint: 'Careers block - department tabs over an accordion of job openings',
  },
  REELS_STRIP: {
    label: 'Creator reels',
    icon: Clapperboard,
    hint: '1-20 vertical cards - reel link or uploaded video, tag chip + package CTA',
  },
  AWARDS_STATS: {
    label: 'Award badge + stat tiles',
    icon: Award,
    hint: 'Award artwork on the left, icon tiles on the right (Google rating live, followers, years, awards)',
    group: REVIEWS_GROUP,
  },
  VIDEO_REVIEWS: {
    label: 'Video reviews grid',
    icon: Clapperboard,
    hint: 'Filterable grid of vertical video reviews (reel link or upload) with "Load more"',
    group: REVIEWS_GROUP,
  },
  TRAVELLER_REVIEWS: {
    label: 'Traveller reviews',
    icon: Quote,
    hint: 'Stacked review cards - avatar, rating, booked package, photos - merged with live Google reviews',
    group: REVIEWS_GROUP,
  },
  HANDPICKED_STAYS: {
    label: 'Handpicked stays',
    icon: BedDouble,
    hint: '1-20 hotel cards - image, name, occupancy + price chip',
  },
  EXPLORE_THEMES: {
    label: 'Explore by themes',
    icon: Compass,
    hint: '1-20 theme tiles - image + label',
  },
  STAY_COLLECTION: {
    label: 'Live hotel cards',
    icon: Hotel,
    hint: 'A row of TBO hotels with live prices - your picks, trending, or top rated in a city',
    group: FUNNEL_GROUP,
  },
  STAY_DESTINATIONS: {
    label: 'Hotel destinations',
    icon: MapPinned,
    hint: 'City tiles with a live stay count from TBO - photo tiles or a compact list',
    group: FUNNEL_GROUP,
  },
  FLIGHT_DEALS: {
    label: 'Flight deals',
    icon: Plane,
    hint: 'Route cards with the cheapest date to fly, from the TBO fare calendar',
    group: FUNNEL_GROUP,
  },
  ACTIVITY_PICKS: {
    label: 'Activity lists',
    icon: Ticket,
    hint: 'Up to three columns of typed activities - thumbnail, name, price, link or enquiry',
    group: FUNNEL_GROUP,
  },
  PACKAGE_CAROUSELS: {
    label: 'Package carousels',
    icon: Boxes,
    hint: 'One or more theme-tagged package carousels (like the Destination page sliders)',
    group: TOURISM_GROUP,
  },
  QUICK_INFO: {
    label: 'Quick information',
    icon: Info,
    hint: 'Bordered key/value panel - e.g. countries covered, best time, duration, price',
    group: TOURISM_GROUP,
  },
  BEST_TIME: {
    label: 'Best time to visit',
    icon: CalendarRange,
    hint: 'JAN-DEC month strip tinted by season, a best-window callout + trust stats',
    group: TOURISM_GROUP,
  },
  MONTH_BY_MONTH: {
    label: 'Month by month',
    icon: CalendarDays,
    hint: 'One detail card per month - photo, temperature, pros/cons, activities, festivals + a travel tip',
    group: TOURISM_GROUP,
  },
  TRAVEL_STYLES: {
    label: 'Best time by travel style',
    icon: HeartHandshake,
    hint: 'Tiles per travel style - icon, name, one line of copy + the months that suit it',
    group: TOURISM_GROUP,
  },
  RECOMMENDED_PACKAGES: {
    label: 'Recommended packages',
    icon: Boxes,
    hint: 'Hand-picked package cards for a destination + a "View all" link - read live from Packages',
    group: TOURISM_GROUP,
  },
  PLACES_GRID: {
    label: 'Places to visit',
    icon: MapPinned,
    hint: 'Attraction cards - image, name, description, best time / timings / entry fee',
    group: TOURISM_GROUP,
  },
  EVENTS_GRID: {
    label: 'Discover events',
    icon: Ticket,
    hint: 'Event cards - image, name, description + a Key Capabilities bullet list',
    group: TOURISM_GROUP,
  },
  ACTIVITIES_GRID: {
    label: 'Your people, your place',
    icon: HeartHandshake,
    hint: 'Travel-style bubbles - circular image + label (Family, Couple, Solo …)',
    group: TOURISM_GROUP,
  },
  VISION_MISSION: {
    label: 'Vision & Mission',
    icon: Target,
    hint: 'Two-column Vision / Mission statement card - heading + paragraph each',
    group: ABOUT_GROUP,
  },
  CELEBRATION: {
    label: 'Celebration / milestone',
    icon: PartyPopper,
    hint: 'Anniversary heading + description + an image carousel (e.g. "15 Years")',
    group: ABOUT_GROUP,
  },
  CALLBACK_CHAT: {
    label: 'Callback + chat form',
    icon: PhoneCall,
    hint: 'Compact lead form - Name/Phone/Email + "Request a callback" & "Chat With Us" (WhatsApp)',
  },
  LANDING_PACKAGES: {
    label: 'Landing packages',
    icon: Boxes,
    hint: 'Hand-written package cards - type every field; not linked to the Packages master',
  },
};

export const sectionLabel = (type: string): string =>
  SECTION_META[type as SectionType]?.label ?? type;

export const sectionIcon = (type: string): LucideIcon =>
  SECTION_META[type as SectionType]?.icon ?? LayoutGrid;

export type TemplateTone = 'accent' | 'info' | 'success' | 'neutral' | 'warning';

export interface SectionTemplate {
  id: string;
  name: string;
  description: string;
  badges: string[];
  tone: TemplateTone;
  sections: SectionType[];
}

export const SECTION_TEMPLATES: SectionTemplate[] = [
  {
    id: 'tpl-landing',
    name: 'Landing combo',
    description: 'Hero → Service icons → Featured destination → CTA banner',
    badges: ['Most used', '4 sections'],
    tone: 'accent',
    sections: ['HERO', 'SERVICE_ICONS_ROW', 'FEATURED_CATEGORY', 'CTA_BANNER'],
  },
  {
    id: 'tpl-social',
    name: 'Social proof stack',
    description: 'Star endorsements → Client stories → Press logos',
    badges: ['3 sections'],
    tone: 'info',
    sections: ['STAR_ENDORSEMENTS', 'CLIENT_STORIES', 'PRESS_LOGOS'],
  },
  {
    id: 'tpl-promo',
    name: 'Promo landing',
    description: 'Hero → CTA banner → Feature list → Callback form',
    badges: ['High converting', '4 sections'],
    tone: 'success',
    sections: ['HERO', 'CTA_BANNER', 'FEATURE_LIST', 'CALLBACK_FORM'],
  },
  {
    id: 'tpl-about',
    name: 'About / brand',
    description: 'Hero → Feature list → Awards block → Press logos',
    badges: ['4 sections'],
    tone: 'neutral',
    sections: ['HERO', 'FEATURE_LIST', 'AWARDS_BLOCK', 'PRESS_LOGOS'],
  },
  {
    id: 'tpl-contact',
    name: 'Contact lead-gen',
    description: 'Callback form → Partners logos',
    badges: ['2 sections'],
    tone: 'warning',
    sections: ['CALLBACK_FORM', 'PARTNERS_LOGOS'],
  },
  {
    id: 'tpl-reviews',
    name: 'Reviews page',
    description:
      'Hero → Award stats → Video reviews → Traveller reviews → Gallery → Feature cards → Partners → Rich text → FAQ',
    badges: ['Google reviews', '9 sections'],
    tone: 'info',
    sections: [
      'HERO',
      'AWARDS_STATS',
      'VIDEO_REVIEWS',
      'TRAVELLER_REVIEWS',
      'GALLERY',
      'FEATURE_CARDS',
      'PARTNERS_LOGOS',
      'RICH_TEXT',
      'FAQ_LIST',
    ],
  },
];

/** Short one-line summary of a section's content for the collapsed accordion row. */
export function summaryFor(type: string, data: unknown): string {
  const d = (data ?? {}) as Record<string, unknown>;
  const count = (key: string): number => (Array.isArray(d[key]) ? (d[key] as unknown[]).length : 0);
  switch (type) {
    // HERO / CTA_BANNER / CALLBACK_FORM headings now live on the section's
    // title column; the caller shows `section.title || summaryFor(...)`, so
    // this branch is only the no-title fallback.
    case 'HERO':
      return '(no title)';
    case 'SERVICE_ICONS_ROW':
      return `${count('items')} icons`;
    case 'STAR_ENDORSEMENTS':
      return `${count('items')} travellers`;
    case 'FEATURED_CATEGORY':
      return (d.category_label as string) || '(no label)';
    case 'EDITORS_PICKS': {
      const picks = count('pick_blog_ids');
      const featured = d.featured_blog_id ? 1 : 0;
      return `${featured + picks} blog post${featured + picks === 1 ? '' : 's'}`;
    }
    case 'EXPLORE_STORIES': {
      const groups = [
        d.show_destination_filter !== false && 'Destination',
        d.show_interest_filter !== false && 'Category',
        d.show_theme_filter !== false && 'Theme',
      ].filter(Boolean);
      return groups.length ? `Filters: ${groups.join(', ')}` : 'Story filter';
    }
    case 'CTA_BANNER':
      return '(no title)';
    case 'FEATURE_LIST':
      return `${count('items')} features`;
    case 'CLIENT_STORIES':
      return `${count('items')} stories`;
    case 'REVIEWS': {
      const n = count('items');
      return `${n} review${n === 1 ? '' : 's'}`;
    }
    case 'MICE_CLIENT_STORIES':
      return `${count('items')} story ${count('items') === 1 ? 'card' : 'cards'}`;
    case 'GALLERY':
      return `${count('photo_media_ids')} photos`;
    case 'AWARDS_BLOCK':
      return `${count('items')} awards`;
    case 'CALLBACK_FORM':
      return '(no title)';
    case 'MICE_FORM':
      return '(no title)';
    case 'FAQ_LIST': {
      const n = count('items');
      return `${n} question${n === 1 ? '' : 's'}`;
    }
    case 'PARTNERS_LOGOS':
      return `${count('partner_ids')} logos`;
    case 'MICE_ACCREDITATIONS': {
      const n = count('media_ids');
      return `${n} ${n === 1 ? 'logo' : 'logos'}`;
    }
    case 'MICE_PARTNERS_MARQUEE': {
      const n = count('media_ids');
      return `${n} ${n === 1 ? 'logo' : 'logos'}`;
    }
    case 'MICE_HERO': {
      const n = count('stats');
      return n ? `${n} ${n === 1 ? 'stat' : 'stats'}` : 'Corporate hero';
    }
    case 'MICE_SERVICES': {
      const n = count('items');
      return `${n} ${n === 1 ? 'pillar' : 'pillars'}`;
    }
    case 'MICE_CASE_STUDIES': {
      const n = count('items');
      return `${n} case ${n === 1 ? 'study' : 'studies'}`;
    }
    case 'MICE_DESTINATIONS': {
      const n = count('groups');
      return `${n} ${n === 1 ? 'group' : 'groups'}`;
    }
    case 'PRESS_LOGOS':
      return `${count('press_mention_ids')} mentions`;
    case 'LOCATIONS': {
      const n = count('branch_ids');
      return `${n} office${n === 1 ? '' : 's'}`;
    }
    case 'TEXT_BLOCK': {
      const body = typeof d.body === 'string' ? d.body.trim() : '';
      if (!body) return '(no text)';
      return body.length > 60 ? `${body.slice(0, 60)}…` : body;
    }
    case 'TEAM_PROFILES': {
      const n = count('items');
      return `${n} ${n === 1 ? 'profile' : 'profiles'}`;
    }
    case 'FULL_IMAGE':
      return d.image_media_id ? 'Full-width image' : '(no image)';
    case 'FEATURE_CARDS': {
      const n = count('items');
      return `${n} ${n === 1 ? 'card' : 'cards'}`;
    }
    case 'PROCESS_STEPS': {
      const n = count('items');
      return `${n} ${n === 1 ? 'step' : 'steps'}`;
    }
    case 'VALUE_PROPS': {
      const n = count('items');
      return `${n} value ${n === 1 ? 'prop' : 'props'}`;
    }
    case 'STATS_STRIP': {
      const n = count('items');
      return `${n} ${n === 1 ? 'stat' : 'stats'}`;
    }
    case 'IMAGE_MARQUEE': {
      const n = count('photo_media_ids');
      return `${n} ${n === 1 ? 'image' : 'images'}`;
    }
    case 'OPEN_POSITIONS': {
      const n = count('items');
      return `${n} ${n === 1 ? 'opening' : 'openings'}`;
    }
    case 'REELS_STRIP':
      return `${count('items')} reels`;
    case 'AWARDS_STATS': {
      const n = count('items');
      return `${d.badge_media_id ? 'badge + ' : ''}${n} ${n === 1 ? 'tile' : 'tiles'}`;
    }
    case 'VIDEO_REVIEWS': {
      const n = count('items');
      return `${n} video ${n === 1 ? 'review' : 'reviews'}`;
    }
    case 'TRAVELLER_REVIEWS': {
      const n = count('items');
      return `${n} ${n === 1 ? 'review' : 'reviews'}${d.show_google_reviews === false ? '' : ' + Google'}`;
    }
    case 'HANDPICKED_STAYS':
      return `${count('items')} stays`;
    case 'EXPLORE_THEMES':
      return `${count('items')} themes`;
    case 'STAY_COLLECTION': {
      const how =
        d.mode === 'trending' ? 'Trending' : d.mode === 'top_rated' ? 'Top rated' : 'Picked';
      return `${how} hotels · ${count('items')} picks · live prices`;
    }
    case 'STAY_DESTINATIONS':
      return `${d.mode === 'trending' ? 'Trending' : 'Picked'} cities · ${count('items')} picks`;
    case 'FLIGHT_DEALS':
      return `${d.mode === 'trending' ? 'Trending' : 'Picked'} routes · ${count('items')} picks`;
    case 'ACTIVITY_PICKS': {
      const n = count('groups');
      return `${n} ${n === 1 ? 'column' : 'columns'}`;
    }
    case 'QUICK_INFO': {
      const n = count('items');
      return `${n} ${n === 1 ? 'row' : 'rows'}`;
    }
    case 'BEST_TIME': {
      const n = count('months');
      return n ? `${n} ${n === 1 ? 'month' : 'months'}` : 'Best time to visit';
    }
    case 'MONTH_BY_MONTH': {
      const n = count('items');
      return `${n} ${n === 1 ? 'month' : 'months'}`;
    }
    case 'TRAVEL_STYLES': {
      const n = count('items');
      return `${n} ${n === 1 ? 'style' : 'styles'}`;
    }
    case 'RECOMMENDED_PACKAGES': {
      const n = count('package_ids');
      return n ? `${n} ${n === 1 ? 'package' : 'packages'}` : 'Auto-filled by destination';
    }
    case 'PLACES_GRID': {
      const n = count('items');
      return `${n} ${n === 1 ? 'place' : 'places'}`;
    }
    case 'EVENTS_GRID': {
      const n = count('items');
      return `${n} ${n === 1 ? 'event' : 'events'}`;
    }
    case 'ACTIVITIES_GRID': {
      const n = count('items');
      return `${n} ${n === 1 ? 'item' : 'items'}`;
    }
    case 'PACKAGE_CAROUSELS': {
      const n = count('groups');
      return `${n} ${n === 1 ? 'carousel' : 'carousels'}`;
    }
    case 'VISION_MISSION':
      return 'Vision & Mission';
    case 'CELEBRATION': {
      const n = count('photo_media_ids');
      return `${n} ${n === 1 ? 'photo' : 'photos'}`;
    }
    case 'CALLBACK_CHAT':
      return 'Callback + chat form';
    case 'LANDING_PACKAGES': {
      const n = count('items');
      return `${n} ${n === 1 ? 'card' : 'cards'}`;
    }
    default:
      return '';
  }
}
